import styled from '@emotion/styled'
import { ExtranetTicketCommentDto } from '@pharmac-extranet/extranet-api'
import { formatDateTimeString, useBreakpoint } from '@pharmac-extranet/shared'
import { joinClasses } from '../../../../../packages/shared/src/components/extranet-table/joinClasses'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'

const CommentContainer = styled.div`
  width: 100%;

  margin-left: 10px;

  :not(last-of-type) {
    margin-bottom: var(--spacing-medium);
  }

  .tc__author {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 30px;
    justify-content: flex-start;
    align-items: center;

    &--avatar {
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      height: 30px;
      width: 30px;
      background-color: var(--color-secondary);
      font-weight: 600;
      font-size: 16px;
      line-height: 50px;
      color: var(--color-white);
    }

    &--name,
    &--date {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-left: var(--spacing-medium);
    }

    &--my-name {
      color: var(--color-secondary);
      font-weight: 600;
    }
  }

  .tc__text {
    padding-right: var(--spacing-small);
    padding-top: var(--spacing-small);
    margin-left: calc(var(--spacing-small) + 35px);
    font-weight: 500;
  }

  &.cs {
    border-radius: 12px;
    border: 2px solid var(--color-accent-4);
    background-color: #f8f3f9;
    padding: var(--spacing-small);
    box-shadow: 2px 2px 4px 0px rgba(0, 0, 0, 0.05);

    margin-left: 0;

    .tc__author {
      &--avatar {
        background-color: var(--color-primary);
      }
    }

    .tc__text {
      padding-bottom: var(--spacing-small);
    }

    .pfo__text {
      color: var(--color-primary);
      font-weight: 600;
      padding-right: var(--spacing-small);
    }
  }
`

type TicketCommentProps = {
  comment: ExtranetTicketCommentDto
}

export const TicketComment = ({ comment }: TicketCommentProps) => {
  const { user } = useAuthenticationContext()
  const { minDesktop } = useBreakpoint()

  const isAuthorComment = !!user?.keycloakId && comment.author.includes(user.keycloakId)

  const uuidRegex =
    /(__[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12})/

  const isCustomerComment = uuidRegex.test(comment.author)

  const parseInitials = (name: string) => {
    const initials = name
      .match(/(^\S\S?|\s\S)?/g)!
      .map((v) => v.trim())
      .join('')
      .match(/(^\S|\S$)?/g)!
      .join('')
      .toLocaleUpperCase()

    return initials.split('').reverse()
  }

  const parseExtranetAuthor = (author: string) => {
    const split = author.split('__')
    const name = split[0].replace('+', ' ')
    const keycloakId = split[1]

    if (keycloakId) return name.split(' ').reverse().join(' ')

    return name
  }

  const author = parseExtranetAuthor(comment.author)
  const initials = parseInitials(author)

  return (
    <CommentContainer className={!isCustomerComment ? 'cs' : ''}>
      <div className="tc__author">
        <div className="tc__author--avatar">{initials}</div>
        <div style={{ display: 'flex', flexDirection: minDesktop ? 'row' : 'column' }}>
          <div
            className={joinClasses(
              'tc__author--name',
              isCustomerComment && isAuthorComment && 'tc__author--my-name'
            )}
          >
            {!isCustomerComment && <div className="pfo__text">Pharmac Finland Oy</div>}
            {author}
          </div>
          <div className="tc__author--date">{formatDateTimeString(comment.date)}</div>
        </div>
      </div>
      <div
        style={{
          marginTop: minDesktop ? undefined : 'var(--spacing-small)',
          whiteSpace: 'pre-line',
        }}
        className="tc__text"
      >
        {comment.value}
      </div>
    </CommentContainer>
  )
}
